import React, { useState, useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useParams, Link, useHistory } from 'react-router-dom';
import { CancelTokenSource } from 'axios';
import { RootState } from 'modules';
import { showConfirm } from 'components/modal/showConfirm';
import apiClient from 'libs/api/apiClient';
import { getSubThemeList, getSubThemeDetail } from 'libs/api/learning';
import { usePermission } from 'libs/hooks';
import { TSubTheme, TLearningParams } from 'modules/learning';
import { CWindowWidth } from 'constants/base.const';
import { ENotificationType } from 'enums/base.enum';
import { ERoute } from 'enums/route.enum';
import { Button, Row, Typography } from 'antd';
import styled from 'styled-components';
import { device, PrimaryColor, SecondaryColor } from 'GlobalStyles';
import ExplainTemplate from '../parts/ExplainTemplate';
import RecommendCardBox from '../parts/RecommendCardBox';
import RecommendSubjectList from '../parts/RecommendSubject';
import LearningBoardEditor from './LearningBoardEditor';

const { Title, Text } = Typography;

const SubThemeExplain = () => {
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);

  const [moveDirection, setMoveDirection]: any = useState({
    prev: null,
    next: null,
  });
  const [subThemeDetail, setSubThemeDetail] = useState<TSubTheme>();
  const [subThemeDetailBoard, setSubThemeDetailBoard] = useState<any>();
  const { category, step_id, operation }: TLearningParams = useParams();
  const { adminPermission } = usePermission();
  const history = useHistory();

  const getPrevNext = async () => {
    const { success, data } = await getSubThemeDetail({
      subject: `${category}`,
      is_example: 'False',
      mentoring_field: currentUser?.primary_field === 'finance' ? 'finance' : 'consulting',
    });

    const getPrevNext = (data: any) => {
      for (let i = 0; i < data.length; i++) {
        if (step_id === data[i].id.toString()) {
          if (i === 0 && data.length > 1) {
            setMoveDirection({
              prev: null,
              next: data[i + 1].id.toString(),
            });

            return;
          } else if (i === data.length - 1 && data.length > 1) {
            setMoveDirection({
              prev: data[i - 1].id.toString(),
              next: null,
            });

            return;
          } else if (data.length === 1) {
            setMoveDirection({
              prev: null,
              next: null,
            });

            return;
          } else {
            setMoveDirection({
              prev: data[i - 1].id.toString(),
              next: data[i + 1].id.toString(),
            });

            return;
          }
        }
      }
    };

    if (success) {
      getPrevNext(data);
    }
  };

  useEffect(() => {
    getPrevNext();
  }, [step_id]);

  useEffect(() => {
    const getSubThemeDetail = async () => {
      const { success, data } = await getSubThemeList(step_id);

      if (success) setSubThemeDetail(data);
    };

    if (!currentUser?.primary_field) {
      getSubThemeDetail();
      return;
    } else if (category !== currentUser?.primary_field) {
      let getPrimaryField =
        category === 'fit_interview' ||
        category === 'turnaround_strategy' ||
        category === 'case_interview' ||
        category === 'growth_strategy' ||
        category === 'dd_market_sizing' ||
        category === 'operation_optimization' ||
        category === 'sales_marketing_strategy'
          ? 'consulting'
          : 'finance';

      if (currentUser?.primary_field !== getPrimaryField) {
        history.push(`${ERoute.CSLearningBoard}`);
      } else {
        getSubThemeDetail();
      }
    }
  }, [step_id]);

  const deleteBoard = (id: string, cancel?: CancelTokenSource) => {
    return apiClient.delete(`/drill/board/${id}`, {
      cancelToken: cancel?.token,
    });
  };

  async function onClickDelete() {
    try {
      await deleteBoard(step_id);

      history.push({
        pathname: `${ERoute.CSLearningBoard}/${category}/view`,
        state: {
          notification: {
            type: ENotificationType.Success,
            message: '게시글이 삭제되었습니다.',
          },
        },
      });
    } catch (e) {
      throw e;
    }
  }

  const getDetail = (id: string, cancel?: CancelTokenSource) => {
    return apiClient.get(`/drill/board/${id}`, {
      cancelToken: cancel?.token,
    });
  };

  useEffect(() => {
    const getSubThemeDetailBoard = async () => {
      const { data } = await getDetail(step_id);

      setSubThemeDetailBoard(data);
    };

    if (!currentUser?.primary_field) {
      getSubThemeDetailBoard();
      return;
    } else if (category !== currentUser?.primary_field) {
      console.log('helo');
      let getPrimaryField =
        category === 'fit_interview' ||
        category === 'turnaround_strategy' ||
        category === 'case_interview' ||
        category === 'growth_strategy' ||
        category === 'dd_market_sizing' ||
        category === 'operation_optimization' ||
        category === 'sales_marketing_strategy'
          ? 'consulting'
          : 'finance';

      if (currentUser?.primary_field !== getPrimaryField) {
        history.push(`${ERoute.CSLearningBoard}`);
      } else {
        getSubThemeDetailBoard();
      }
    }
  }, []);

  return (
    <>
      {operation === 'view' && (
        <>
          {subThemeDetail && (
            <ExplainTemplate
              img={subThemeDetail.thumbnail.location}
              pageTitle={subThemeDetail.title}
              pageSubtitle={subThemeDetail.subtitle}
              hasSubject={false}
            >
              <StyledParagraph
                dangerouslySetInnerHTML={{
                  __html: `${subThemeDetail.body_list![0].body}`,
                }}
              />

              {category === 'basic_guide' && !currentUser?.is_finance_frameworks_membership ? (
                <PriceGuideContainer>
                  <PriceGuideBox>
                    <GuidePriceHeader
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                    >
                      <Title>Finance Contents 구매하기</Title>
                    </GuidePriceHeader>

                    <GuidePriceTickets>
                      <div>
                        <PriceGuideTicket>
                          <Title>Finance Frameworks 무제한 이용권</Title>

                          <Text>
                            <span className='discount'>정가 5만원</span>
                            <span className='price'>3</span>
                            <span className='priceTag'>만원 (40% 할인) </span>
                            <span className='period'>/ 3개월</span>
                          </Text>
                        </PriceGuideTicket>

                        {!currentUser?.is_finance_frameworks_membership && (
                          <Link to={'/purchase/finance_frameworks'}>
                            <Button className='secondary'>구매하기</Button>
                          </Link>
                        )}
                      </div>
                    </GuidePriceTickets>
                  </PriceGuideBox>
                </PriceGuideContainer>
              ) : (
                <RecommendSubjectList />
              )}

              {subThemeDetail.example.length > 0 && (
                <RecommendCardBox title='케이스 예제로 더 알아보기' list={subThemeDetail.example} />
              )}

              {adminPermission ? (
                <Row>
                  <Button
                    type='primary'
                    size='large'
                    onClick={() => showConfirm(() => onClickDelete(), '게시글을 삭제하시겠습니까?')}
                    style={{ marginRight: '20px' }}
                  >
                    삭제
                  </Button>
                  <Link to={`${ERoute.CSLearningBoard}/${category}/${step_id}/edit`}>
                    <Button type='primary' size='large'>
                      수정
                    </Button>
                  </Link>
                </Row>
              ) : (
                <Row style={{ marginTop: '30px', marginBottom: '30px' }} justify='space-between'>
                  <Link
                    to={`${ERoute.CSLearningBoard}/${category}/${moveDirection.prev}/view`}
                    onClick={(e) => moveDirection.prev === null && e.preventDefault()}
                  >
                    <Button
                      type='primary'
                      disabled={moveDirection.prev === null}
                      size='large'
                      style={{ marginRight: '20px' }}
                    >
                      이전글
                    </Button>
                  </Link>
                  <Link
                    to={`${ERoute.CSLearningBoard}/${category}/${moveDirection.next}/view`}
                    onClick={(e) => moveDirection.next === null && e.preventDefault()}
                  >
                    <Button disabled={moveDirection.next === null} type='primary' size='large'>
                      다음글
                    </Button>
                  </Link>
                </Row>
              )}
            </ExplainTemplate>
          )}
        </>
      )}

      {operation === 'edit' && (
        <LearningBoardEditor
          pathName={`${ERoute.CSLearningBoard}/${category}/view`}
          operation={operation}
          record={subThemeDetailBoard}
        />
      )}
    </>
  );
};

export default SubThemeExplain;

const maxWidth = CWindowWidth - 300;
const StyledParagraph = styled.div`
  max-width: ${maxWidth};
  width: 55%;
  margin-top: 50px;
  -webkit-user-drag: none;
  -webkit-touch-callout: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  @media screen and ${device.xl} {
    width: 600px;
  }

  @media screen and ${device.lg} {
    width: 560px;
  }

  @media screen and ${device.md} {
    width: 480px;
  }

  @media screen and ${device.sm} {
    width: 310px;
  }

  img {
    max-width: 100%;
    min-width: 650px;
    height: auto;

    @media screen and ${device.lg} {
      width: 560px !important;
      min-width: 0px;
      height: auto;
    }

    @media screen and ${device.md} {
      width: 480px !important;
      height: auto;
    }

    @media screen and ${device.sm} {
      width: 310px !important;
      height: auto;
    }
  }

  iframe {
    max-width: 650px;
    max-height: 364px;

    @media screen and ${device.xl} {
      width: 560px !important;
      height: 314px !important;
    }

    @media screen and ${device.lg} {
      width: 480px !important;
      height: 268px !important;
    }

    @media screen and ${device.md} {
      width: 400px !important;
      height: 224px !important;
    }

    @media screen and ${device.sm} {
      width: 300px !important;
      height: 168px !important;
    }
  }
`;

const PriceGuideContainer = styled.section`
  max-width: 1440px;
  width: 100%;
  margin: 0 auto;
  padding: 50px 50px 70px 50px;

  @media screen and ${device.lg} {
    padding: 35px 20px;
  }

  button.secondary {
    display: block;
    width: 134px;
    height: 44px;
    margin: 0 auto;

    @media screen and ${device.lg} {
      width: 100%;
    }
  }
`;

const PriceGuideBox = styled.div`
  margin-top: 25px;
  padding: 30px 45px;
  border: 1px solid ${SecondaryColor};
  border-radius: 10px;
  background: #ffffff;

  @media screen and ${device.lg} {
    padding: 25px 15px;
  }

  .secondSubTitle {
    display: block;
    margin: 10px 0 0 0;
    color: ${PrimaryColor};
  }
`;

const GuidePriceHeader = styled.header`
  display: flex;
  align-items: center;

  @media screen and ${device.lg} {
    flex-direction: column;
    align-items: flex-start;
  }

  h1.ant-typography {
    margin: 0 10px 0 0;
    color: #141121;
    font-size: 20px;
  }

  span.ant-typography {
    color: #747474;
  }
`;

const GuidePriceTickets = styled.div`
  display: flex;
  justify-content: center;

  @media screen and ${device.lg} {
    flex-direction: column;
  }

  .ant-btn.secondary.purchased {
    width: auto;

    @media screen and ${device.lg} {
      width: 100%;
    }
  }
`;

const PriceGuideTicket = styled.div`
  width: 356px;
  margin: 15px 15px;
  padding: 25px 0;
  border-radius: 10px;
  background: #f9f9f9;
  white-space: pre-wrap;

  @media screen and ${device.xl} {
    width: 260px;
  }

  @media screen and ${device.lg} {
    width: 100%;
    margin: 25px 0;
  }

  h1.ant-typography {
    margin: 0;
    font-size: 18px;
    text-align: center;
    line-height: 26px;
    color: ${PrimaryColor};

    @media screen and ${device.xl} {
      font-size: 16px;
    }

    @media screen and ${device.lg} {
      font-size: 18px;
    }
  }

  span.ant-typography {
    display: block;
    text-align: center;

    .price {
      margin-right: 5px;
      font-size: 48px;
      color: ${SecondaryColor};
    }

    .priceTag {
      font-size: 18px;
      color: ${SecondaryColor};
    }

    .period,
    .discount {
      font-size: 18px;
      color: #747474;
    }

    .discount {
      margin-right: 10px;
      text-decoration: line-through;
    }
  }
`;
